/**
 * Signing up by invite, on every door (GH-871b, DEC-052).
 *
 * While membership is on, a person with no account may make one only with an invite code (a
 * member's, or the one a waitlist admission sent) or the big screen's join token. A sign-in that
 * carries neither answers `invite_required`; one whose code cannot be used answers
 * `invite_unavailable`. Either way **nothing was written and nothing was spent**: the phone code,
 * or the Apple or Google nonce, is still good for its own life (GH-871a, tested server-side), so the
 * same confirmation can be sent again with a code.
 *
 * `DoorAttempt` is that confirmation, held in memory only for the retry — the phone's verification
 * id and code, or the provider's ID token and nonce. It is never stored and never logged, and it is
 * dropped with the sheet.
 */
import type { DonorSessionResponse } from "@givehere/contracts";
import {
  confirmAppleWebSignIn,
  confirmDonorSignIn,
  confirmGoogleWebSignIn,
  DonorApiError,
  rememberToken,
  type SignUpAccess,
} from "./donor-session.js";

/**
 * `invite_required` / `invite_unavailable` from a sign-in door: no account was made. Here rather
 * than in `donor-session.ts`, whose functions the UI tests stub wholesale.
 */
export const isSignUpRefusal = (caught: unknown): caught is DonorApiError =>
  caught instanceof DonorApiError &&
  (caught.code === "invite_required" || caught.code === "invite_unavailable");

export type DoorAttempt =
  | {
      readonly door: "phone";
      readonly verificationId: string;
      readonly code: string;
      readonly phone: string;
    }
  | {
      readonly door: "apple";
      readonly credential: string;
      readonly nonce: string;
      readonly displayName?: string;
    }
  | { readonly door: "google"; readonly credential: string; readonly nonce: string };

/** Only the fields that have a value — an absent field is not sent at all. */
export function accessFields(access: SignUpAccess): SignUpAccess {
  return {
    ...(access.inviteCode ? { inviteCode: access.inviteCode } : {}),
    ...(access.eventJoinToken ? { eventJoinToken: access.eventJoinToken } : {}),
  };
}

/** Send a refused confirmation again with the access it lacked, and remember the device. */
export async function retryDoor(
  attempt: DoorAttempt,
  access: SignUpAccess,
): Promise<DonorSessionResponse> {
  const fields = accessFields(access);
  const issued =
    attempt.door === "phone"
      ? await confirmDonorSignIn({
          verificationId: attempt.verificationId,
          code: attempt.code,
          phone: attempt.phone,
          ...fields,
        })
      : attempt.door === "apple"
        ? await confirmAppleWebSignIn({
            credential: attempt.credential,
            nonce: attempt.nonce,
            ...(attempt.displayName ? { displayName: attempt.displayName } : {}),
            ...fields,
          })
        : await confirmGoogleWebSignIn({
            credential: attempt.credential,
            nonce: attempt.nonce,
            ...fields,
          });
  rememberToken(issued.token);
  return issued;
}
